import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FormikProps } from "formik";
import { DescriptionType } from "../types/profile-form";
import { ProfileFormValues } from "../hooks/useProfileScreen";
import { toast } from "react-toastify";

interface DescriptionProps {
  formik: FormikProps<ProfileFormValues>;
  editable?: boolean;
}

const Description: React.FC<DescriptionProps> = ({
  formik,
  editable = true,
}) => {
  const { values, setFieldValue } = formik;
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Handle Input Change
  const handleChange = (
    index: number,
    field: keyof DescriptionType,
    value: string
  ) => {
    const updatedSections = [...values.description];
    updatedSections[index] = { ...updatedSections[index], [field]: value };
    setFieldValue("description", updatedSections);
  };

  // Add New Section
  const addSection = () => {
    if (values.description.length >= 3) {
      toast.error("You can only add up to 3 sections.");
      return;
    }
    const newSection = {
      heading: "",
      subHeading: "",
      details: "",
    };
    setFieldValue("description", [...values.description, newSection]);
  };

  // Remove Section
  const removeSection = (index: number) => {
    const updatedSections = values.description.filter((_, i) => i !== index);
    setFieldValue("description", updatedSections);
    if (expandedIndex === index) setExpandedIndex(null);
  };

  return (
    <Card sx={{ marginTop: 3, padding: 2, width: "100%" }}>
      <CardContent>
        <Typography
          fontWeight={600}
          variant="h6"
          color="#181D27"
          marginBottom={1}
        >
          Description <Typography variant="caption" color="grey" fontWeight={'bold'} >(optional)</Typography>
          <br />
          <Typography
            fontFamily="Inter"
            fontSize={14}
            color="#535862"
            sx={{
              display: "block",
              textIndent: "-1em",
              paddingLeft: "1em",
              whiteSpace: "pre-line",
            }}
          >
            **This space can be used to share their story and experiences, helping users make informed decisions.
          </Typography>
        </Typography>

        {values?.description?.map((section, index) => (
          <Accordion
            key={index}
            expanded={expandedIndex === index}
            onChange={() => handleExpand(index)}
            sx={{
              marginY: 2,
              "&:before": { display: "none" },
              border: "1px solid #D5D7DA",
              borderRadius: "8px !important",
              boxShadow: "none",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <Typography
                  variant="body1"
                  color="#181D27"
                  fontWeight={600}
                  fontFamily="Inter, sans-serif"
                  fontSize="18px"
                  sx={{ flexShrink: 0 }}
                >
                  Description {index + 1}
                </Typography>

                {section.heading && expandedIndex !== index && (
                  <Typography
                    variant="body2"
                    color="#535862"
                    fontFamily="Inter"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginLeft: 2,
                      maxWidth: "60%",
                    }}
                  >
                    - {section.heading}
                  </Typography>
                )}

                <IconButton
                  aria-label="delete"
                  size="small"
                  disabled={!editable}
                  onClick={() => removeSection(index)}
                  sx={{ marginLeft: "auto" }}
                >
                  <DeleteIcon color={editable ? "error" : "disabled"} />
                </IconButton>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ padding: 2 }}>
              {/* Heading Input */}
              <Box flexBasis={{ xs: "100%", sm: "48%" }}>
                <Typography
                  variant="body1"
                  color="#414651"
                  fontWeight={500}
                  marginBottom={1}
                >
                  Heading *
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Enter heading"
                  fullWidth
                  value={section.heading}
                  disabled={!editable}
                  onChange={(e) =>
                    handleChange(index, "heading", e.target.value)
                  }
                  slotProps={{
                    htmlInput: {
                      maxLength: 30,
                    },
                  }}
                />
                <Typography
                  sx={{ marginTop: "5px", color: "#535862", fontSize: "small" }}
                >
                  {30 - (section.heading.length || 0)} characters left
                </Typography>
              </Box>

              {/* Subheading Input */}
              <Box flexBasis={{ xs: "100%", sm: "48%" }}>
                <Typography
                  variant="body1"
                  color="#414651"
                  fontWeight={500}
                  marginBottom={1}
                  marginTop={2}
                >
                  Subheading
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Enter Subheading"
                  value={section.subHeading}
                  disabled={!editable}
                  onChange={(e) =>
                    handleChange(index, "subHeading", e.target.value)
                  }
                  slotProps={{
                    htmlInput: {
                      maxLength: 40,
                    },
                  }}
                />
                <Typography
                  sx={{ marginTop: "5px", color: "#535862", fontSize: "small" }}
                >
                  {40 - (section.subHeading.length || 0)} characters left
                </Typography>
              </Box>

              {/* Description Input */}
              <Box sx={{ marginTop: "20px" }}>
                <Typography
                  variant="body1"
                  color="#414651"
                  fontWeight={500}
                  marginBottom={1}
                >
                  Details *
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  value={section.details}
                  disabled={!editable}
                  onChange={(e) =>
                    handleChange(index, "details", e.target.value)
                  }
                  slotProps={{
                    htmlInput: {
                      maxLength: 500,
                    },
                  }}
                  placeholder="Enter details (50-100 words)"
                />
                <Typography
                  sx={{ marginTop: "5px", color: "#535862", fontSize: "small" }}
                >
                  {500 - (section.details.length || 0)} characters left
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
        {editable && (
          <Box
            onClick={addSection}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "#414651",
              fontWeight: 500,
              marginTop: "20px",
              border: "1px solid #D5D7DA",
              borderRadius: "10px",
              width: "170px",
              padding: "10px",
              height: "40px",
              fontFamily: "Inter, sans-serif",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f1f1f1" },
            }}
          >
            Add Description+
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default Description;
